import React, { useContext, useEffect, useState } from 'react';

import CurrencyInput from 'react-currency-input-field';
import { NotificationManager } from 'react-notifications';

import { CloseOutlined } from '@ant-design/icons';

import styled from 'styled-components';

import LoanDetails from './LoanDetails';

/** context */
import { UserContext } from '../context/UserContext';

/** helpers */
import { formatUSDCurrency } from '../helpers/format';

/** service */
import { getApprovedAmazonLoanDetails, updateApplication } from '../service/api';

// Modal components
const Modal = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9;
`;

const ModalContainer = styled.div`
  position: fixed;
  box-shadow: 0px 4px 150px rgb(14 10 28 / 5%);
  border-radius: 5px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 430px;
  padding: 40px 90px;
  background: white;
  pointer-events: ${(props) => (props.showLoading ? 'none' : 'all')};

  &.outside {
    opacity: 0.5;
  }
`;

const ModalHeader = styled.div``;

const ModalTitle = styled.h1`
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 32px;
  line-height: 32px;
  color: #462d7a;
  margin: 0;
`;

const CloseIconBox = styled.div`
  width: 21px;
  height: 21px;
  line-height: 21px;
  position: absolute;
  top: 42px;
  right: 41px;
  cursor: pointer;

  svg {
    font-size: 21px;
  }
`;

const ModalContent = styled.div`
  padding-top: 30px;
`;

const FormFieldsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  .loanAmount {
    background: #ffffff;
    border: 2px solid #462d7a;
    box-sizing: border-box;
    border-radius: 4px;

    font-family: Lato;
    font-style: normal;
    font-weight: 800;
    font-size: 32px;
    line-height: 32px;
    letter-spacing: 1.5px;
    color: #462d7a;
  }
`;

const Label = styled.label`
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 19px;
  line-height: 23px;
  color: #462d7a;
  margin-bottom: 8px;
`;

const NoteText = styled.span`
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 16px;
  color: #7e7e7e;
  margin-top: 16px;

  &.error {
    color: #ff7171;
    width: 100%;
    height: 16px;
  }
`;

const SubmitButtonWrapper = styled.div`
  text-align: center;

  button {
    width: 100%;
    background: #462d7a;
    box-sizing: border-box;
    border-radius: 4px;
  }
`;

const ErrorText = styled.p`
  color: red;
  margin-bottom: 20px;

  a {
    background: burlywood;
    cursor: pointer;
  }
`;

export default function ApprovedAmazon({
  approvedRateProperty,
  handleRedirect,
  isConfirmed,
  changeIsConfirmed,
}) {
  const { updateAppData, setLoading } = useContext(UserContext);
  const [loanData, setLoanData] = useState({});
  const [requestedAmount, setRequestedAmount] = useState(0);
  const [openModalChangeLoanAmount, setOpenModalChangeLoanAmount] = useState(false);
  const [loanDetailsVisible, setLoanDetailsVisible] = useState(false);
  const [invalidLoanAmount, setInvalidLoanAmount] = useState(false);
  const [loanDetailsData, setLoanDetailsData] = useState({});
  const [latestLoanDetails, setLatestLoanDetails] = useState(true);

  useEffect(() => {
    setLoanData(approvedRateProperty?.value);
    setRequestedAmount(approvedRateProperty?.value?.requestedAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedRateProperty]);

  useEffect(() => {
    // init
    // get loan details
    fetchApprovedAmazonLoanDetailsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLoanDetailsVisible = () => {
    setLoanDetailsVisible(!loanDetailsVisible);
  };

  const handleOpenModalChangeLoanAmount = () => {
    setInvalidLoanAmount(false);
    setOpenModalChangeLoanAmount(true);
  };

  const handleCloseModalChangeLoanAmount = () => {
    setLoading(false);
    setOpenModalChangeLoanAmount(false);
  };

  const getCurrencyAmount = (amountUSD) => {
    // get amount without currency
    return Number(amountUSD.replace(/[^0-9.-]+/g, ''));
  };

  const handleCurrenyChange = (value, name) => {
    if (
      value === undefined ||
      getCurrencyAmount(value) === 0 ||
      getCurrencyAmount(value) > loanData?.maxApprovedAmount ||
      getCurrencyAmount(value) < loanData?.minApprovedAmount
    ) {
      setInvalidLoanAmount(true);
      return;
    }

    setInvalidLoanAmount(false);
  };

  const updateRequestedAmount = async (newRequestedAmount) => {
    try {
      const updatedData = await updateApplication([
        {
          itemType: approvedRateProperty.itemType,
          dataType: approvedRateProperty.dataType,
          name: approvedRateProperty.name,
          value: {
            ...approvedRateProperty.value,
            requestedAmount: newRequestedAmount,
          },
        },
      ]);
      if (updatedData.data) {
        updateAppData(updatedData.data);
        // update loan amount
        setRequestedAmount(newRequestedAmount);
        // update loan details
        fetchApprovedAmazonLoanDetailsData();
      }
    } catch (error) {
      NotificationManager.warning(
        'Error while updating the requested amount. Please try again later!',
        'Warning'
      );
      setLoading(false);
    }
  };

  const fetchApprovedAmazonLoanDetailsData = async () => {
    try {
      const response = await getApprovedAmazonLoanDetails();
      if (response && response.data) {
        const data = response.data;
        setLoanDetailsData({
          apr: data.apr,
          disbursedAmount: data.disbursedAmount,
          loanAmount: data.loanAmount,
          loanFee: data.loanFee,
          loanRate: data.loanRate,
          loanTerm: data.loanTerm,
          monthlyPayment: data.monthlyPayment,
          prePaymentPenalty: data.prePaymentPenalty,
        });
        setLatestLoanDetails(true);
        // close modal
        handleCloseModalChangeLoanAmount();
      } else {
        setLatestLoanDetails(false);
        setLoading(false);
        NotificationManager.warning('Error while fetching the updated loan data.', 'Warning');
      }
    } catch (error) {
      setLatestLoanDetails(false);
      setLoading(false);
      NotificationManager.warning('Error while fetching the updated loan data.', 'Warning');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    let newLoanAmount = getCurrencyAmount(event.target.loanAmount.value);
    // update loan details
    updateRequestedAmount(newLoanAmount);
  };

  const refreshPage = () => {
    window.location.reload(false);
  };

  return (
    <div>
      <div className="amazon-approved">
        <div className="section">
          {!approvedRateProperty && (
            <ErrorText>
              Network error, please refresh page or click{' '}
              <a onClick={refreshPage}>&nbsp;&nbsp;&nbsp;here&nbsp;&nbsp;&nbsp;</a>
            </ErrorText>
          )}
        </div>
        <div className="section">
          <h2 className="title">
            Congrats! You are approved for a loan up to{' '}
            {formatUSDCurrency(loanData?.maxApprovedAmount)}
          </h2>
        </div>
        <div className="divider" />
        <div className="section">
          <div className="loan-amount">
            <div className="loan-amount-data">
              <div className="loan-amount-data__sum">
                {formatUSDCurrency(loanData?.requestedAmount)}
              </div>
              <button
                className="loan-amount-data__change"
                onClick={handleOpenModalChangeLoanAmount}
                disabled={!approvedRateProperty}
              >
                Change loan amount
              </button>
            </div>
            {!loanDetailsVisible && (
              <button
                className="loan-amount-viewdetails"
                onClick={toggleLoanDetailsVisible}
                disabled={!approvedRateProperty}
              >
                View Loan Details
              </button>
            )}

            {loanDetailsVisible && (
              <LoanDetails
                loanDetailsData={loanDetailsData}
                toggleLoanDetailsVisible={toggleLoanDetailsVisible}
                latestLoanDetails={latestLoanDetails}
                fetchApprovedAmazonLoanDetailsData={fetchApprovedAmazonLoanDetailsData}
              />
            )}
            <div className="checkbox">
              <div className="checkbox-wrapper">
                <label htmlFor="confirm-amount">
                  <input
                    type="checkbox"
                    id="confirm-amount"
                    name="confirm-amount-check"
                    checked={isConfirmed}
                    onChange={changeIsConfirmed}
                    disabled={!approvedRateProperty}
                  />
                  <span>I confirm that I have selected {formatUSDCurrency(requestedAmount)}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="divider" />
        <div className="section">
          <div className="next-step">
            <h2>Next Steps</h2>
            <p className="next-step__meta">
              Please go back to Amazon Seller Centeral and lock your bank account by clicking the
              button below.
            </p>
            <button
              className="primary-btn"
              disabled={!isConfirmed || !approvedRateProperty}
              onClick={handleRedirect}
            >
              Lock Amazon Seller Central Bank Account
            </button>
          </div>
        </div>
      </div>

      {openModalChangeLoanAmount && (
        <Modal>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>Change Loan Amount</ModalTitle>
              <CloseIconBox onClick={handleCloseModalChangeLoanAmount}>
                <CloseOutlined />
              </CloseIconBox>
            </ModalHeader>
            <ModalContent>
              <form onSubmit={handleSubmit}>
                <FormFieldsContainer>
                  <Label htmlFor="loanAmount">Enter Amount</Label>
                  <CurrencyInput
                    prefix="$"
                    id="loanAmount"
                    name="loanAmount"
                    placeholder="Loan Amount"
                    decimalScale={2}
                    defaultValue={loanData?.requestedAmount}
                    onValueChange={(value, name) => handleCurrenyChange(value, name)}
                    className="loanAmount"
                    allowNegativeValue={false}
                    intlConfig={{ locale: 'en-US', currency: 'USD' }}
                  />
                  <NoteText className="error">
                    {invalidLoanAmount
                      ? 'Loan Amount should be between ' +
                        formatUSDCurrency(loanData?.minApprovedAmount) +
                        ' and ' +
                        formatUSDCurrency(loanData?.maxApprovedAmount) +
                        '.'
                      : ''}
                  </NoteText>
                  <NoteText>Note that amount are rounded up to the nearest hundred</NoteText>
                </FormFieldsContainer>
                <SubmitButtonWrapper>
                  <button type="submit" className="btn primary-btn" disabled={invalidLoanAmount}>
                    Update Loan
                  </button>
                </SubmitButtonWrapper>
              </form>
            </ModalContent>
          </ModalContainer>
        </Modal>
      )}
    </div>
  );
}
